import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { get } from "lodash";
import ApolloContext from "@tvg/utils/apolloContext";
import ApolloContextClients from "@tvg/utils/types/ApolloContext";
import { AlertInline, Modal } from "@tvg/design-system";
import { RaceProgram, RaceStatusEnum } from "@tvg/ts-types/Race";
import { BetBasicInfo } from "@tvg/ts-types/Bet";
import { isRaceOff } from "@tvg/sh-lib-my-bets/utils/raceDetails";
import { getProgramPageMessages } from "../../store/selectors";
import BetInfo from "../Main/MyBetsCard/components/BetInfo";
import BetCancelModal from "../Main/MyBetsCard/components/BetCancelModal";
import useCurrentRaceWagers from "../../hooks/useCurrentRaceWagers";
import {
  formatCurrencyTotal,
  getBetValue,
  getTotalWinAmount
} from "../Main/MyBetsCard/helpers";
import { CURRENCY_THRESHOLD } from "../Main/MyBetsCard/constants";
import { GlobalStyle } from "./styled-components";

export interface BetsModalProps {
  isOpen: boolean;
  onClose: () => void;
  race: RaceProgram;
  races: Array<RaceProgram>;
  onRepeatWager: (betBasicInfo: BetBasicInfo) => void;
}

const BetsModal = ({
  isOpen,
  onClose,
  race,
  races,
  onRepeatWager
}: BetsModalProps) => {
  const programPageMessages = useSelector(getProgramPageMessages);
  const [wagerSelected, setWagerSelected] = useState<BetBasicInfo>();
  const [totalWinAmount, setTotalWinAmount] = useState(0);
  const { behgClient } = useContext<ApolloContextClients>(ApolloContext);
  const { trackCode, raceNumber, raceWagersSummary, raceWagers } =
    useCurrentRaceWagers({
      race,
      behgClient
    });

  const betValue = getBetValue(trackCode, raceNumber, race.raceDate || "");
  const { totalGambledCount, totalGambledAmount } = raceWagersSummary;

  useEffect(() => {
    if (isRaceOff(race.status?.code || RaceStatusEnum.OPEN)) {
      const winAmount = getTotalWinAmount(raceWagers);
      setTotalWinAmount(+winAmount);
    } else {
      setTotalWinAmount(0);
    }
  }, [raceWagers, race.status?.code]);

  useEffect(() => {
    if (raceWagers.length === 0) {
      onClose();
    }
  }, [raceWagers]);

  return (
    <>
      <GlobalStyle isOpen={isOpen} />
      <Modal
        isOpen={isOpen}
        title={`${race.track?.trackName} - R${raceNumber} bets`}
        subtitle={`${totalGambledCount} ${
          totalGambledCount > 1 ? "bets" : "bet"
        } | ${formatCurrencyTotal(
          totalGambledAmount,
          CURRENCY_THRESHOLD
        )} staked`}
        type="flexible"
        onClose={onClose}
        px={0}
        pt={0}
      >
        <>
          {totalWinAmount > 0 && (
            <AlertInline
              message={get(
                programPageMessages,
                "betList.winningsMessage",
                ""
              ).replace("$X", `$${totalWinAmount.toFixed(2)}`)}
              variant="success"
              qaLabel="alert-inline"
              type="fixed"
              icon="cup"
            />
          )}
          {raceWagers.map((wager, wagerIndex) => (
            <BetInfo
              key={`bet-${wager.serialNumber}`}
              mt={wagerIndex ? "0" : "space-4"}
              race={race}
              races={races}
              wager={wager}
              betValue={betValue}
              onRepeatWager={onRepeatWager}
              onCancelWager={setWagerSelected}
              qaLabel={`race-bet-cell-${wagerIndex}`}
            />
          ))}
        </>
      </Modal>
      <BetCancelModal
        betValue={betValue}
        wagerSelected={wagerSelected}
        races={races}
        behgClient={behgClient}
      />
    </>
  );
};

export default BetsModal;
